import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from '../legacy-ball/table-management/designTokens';

/* ─── Types ─── */

interface EventData {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface EventTask {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  dueDate: string;
  completedAt?: string;
  event?: { id: string; name: string };
  subcommittee?: { id: string; name: string };
  assignee?: { id: string; firstName: string; lastName: string };
  assignees?: { member: { id: string; firstName: string; lastName: string }; role?: { id: string; name: string } | null }[];
}

interface DayGroup {
  dateKey: string;
  dateLabel: string;
  tasks: (EventTask & { eventName: string; eventTime: string })[];
}

/* ─── Utilities ─── */

function formatDateLong(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function getDateKey(dateStr: string): string {
  return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
}

/* ─── Status / Priority Config ─── */

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  ToDo: { bg: '#f3f4f6', text: '#6b7280', label: 'To Do' },
  InProgress: { bg: '#fffbeb', text: '#d97706', label: 'In Progress' },
  Done: { bg: '#ecfdf5', text: '#059669', label: 'Done' },
  Cancelled: { bg: '#fef2f2', text: '#dc2626', label: 'Cancelled' },
};

const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  Critical: { color: '#dc2626', label: 'Critical' },
  High: { color: '#ea580c', label: 'High' },
  Medium: { color: '#d97706', label: 'Medium' },
  Low: { color: '#6b7280', label: 'Low' },
};

/* ─── Styles ─── */

const s: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  daySection: { marginBottom: '0.5rem' },
  dayHeader: {
    fontSize: '1rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 0.75rem 0',
    paddingBottom: '0.35rem',
    borderBottom: `2px solid ${PRIMARY}`,
  },
  twoPane: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.2fr',
    gap: '1rem',
    minHeight: '180px',
  },
  twoPaneMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    maxHeight: '400px',
    overflowY: 'auto',
    paddingRight: '0.25rem',
  },
  taskPill: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
    transition: 'border-color 0.15s',
  },
  taskPillActive: {
    backgroundColor: BG_CARD,
    border: `2px solid ${PRIMARY}`,
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
    boxShadow: '0 2px 8px rgba(26,107,60,0.1)',
  },
  pillEventName: {
    fontSize: '0.68rem',
    color: TEXT_MUTED,
    fontWeight: 500,
    marginBottom: '0.15rem',
  },
  pillTaskTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.1rem',
  },
  pillTime: {
    fontSize: '0.68rem',
    color: TEXT_MUTED,
  },
  detailPane: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1.25rem',
    minHeight: '180px',
  },
  detailTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.5rem 0',
  },
  detailSection: {
    marginBottom: '0.75rem',
  },
  detailLabel: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    marginBottom: '0.2rem',
  },
  detailValue: {
    fontSize: '0.85rem',
    color: TEXT_DARK,
    lineHeight: 1.5,
  },
  badge: {
    display: 'inline-block',
    fontSize: '0.68rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '8px',
    marginRight: '0.4rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  detailEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
};

/* ─── Component ─── */

export function GxJourneyTab() {
  const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
  const [allTasks, setAllTasks] = useState<DayGroup['tasks']>([]);
  const [subcommittees, setSubcommittees] = useState<{ id: string; name: string }[]>([]);
  const [subcommitteeFilter, setSubcommitteeFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const [eventsData, tasksData, subcommitteesData] = await Promise.all([
          apiClient<EventData[]>('/events'),
          apiClient<EventTask[]>('/event-tasks').catch(() => [] as EventTask[]),
          apiClient<{ id: string; name: string }[]>('/subcommittees').catch(() => []),
        ]);

        const events = Array.isArray(eventsData) ? eventsData : [];
        const tasks = Array.isArray(tasksData) ? tasksData : [];

        // Set subcommittees from API
        if (Array.isArray(subcommitteesData) && subcommitteesData.length > 0) {
          setSubcommittees(subcommitteesData.sort((a, b) => a.name.localeCompare(b.name)));
        }

        // Build event lookup
        const eventMap = new Map<string, EventData>();
        events.forEach((e) => eventMap.set(e.id, e));

        // Enrich tasks — only include Weekend category tasks
        const weekendTasks = tasks.filter((t) => t.category === 'Weekend');
        const dateMap = new Map<string, DayGroup['tasks']>();

        for (const task of weekendTasks) {
          const evt = task.event ? eventMap.get(task.event.id) : null;
          const dateKey = evt ? getDateKey(evt.date) : getDateKey(task.dueDate);
          const eventName = evt?.name || task.event?.name || 'General';
          const eventTime = evt ? `${formatTime(evt.startTime)} – ${formatTime(evt.endTime)}` : '';

          if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
          dateMap.get(dateKey)!.push({ ...task, eventName, eventTime });
        }

        // Only show pills for subcommittees that have Weekend tasks
        const weekendScIds = new Set(weekendTasks.map((t) => t.subcommittee?.id).filter(Boolean));
        if (Array.isArray(subcommitteesData) && subcommitteesData.length > 0) {
          setSubcommittees(subcommitteesData.filter((sc) => weekendScIds.has(sc.id)).sort((a, b) => a.name.localeCompare(b.name)));
        }

        // Sort dates and build groups
        const sortedDates = [...dateMap.keys()].sort();
        const groups: DayGroup[] = sortedDates.map((dateKey) => ({
          dateKey,
          dateLabel: formatDateLong(dateKey),
          tasks: dateMap.get(dateKey)!,
        }));

        // Store flat list for filtering
        const flat = groups.flatMap((g) => g.tasks.map((t) => ({ ...t, _dateKey: g.dateKey } as any)));
        setAllTasks(flat);

        setDayGroups(groups);

        // Auto-select first task
        if (groups.length > 0 && groups[0].tasks.length > 0) {
          setSelectedTaskId(groups[0].tasks[0].id);
        }
      } catch {
        setError('Failed to load journey data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Rebuild day groups when filter changes
  useEffect(() => {
    const filtered = subcommitteeFilter
      ? allTasks.filter((t) => t.subcommittee?.id === subcommitteeFilter)
      : allTasks;

    const dateMap = new Map<string, DayGroup['tasks']>();
    for (const task of filtered) {
      const dateKey = (task as any)._dateKey || getDateKey(task.dueDate);
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(task);
    }

    const sortedDates = [...dateMap.keys()].sort();
    const groups: DayGroup[] = sortedDates.map((dk) => ({
      dateKey: dk,
      dateLabel: formatDateLong(dk),
      tasks: dateMap.get(dk)!,
    }));

    setDayGroups(groups);
    if (groups.length > 0 && groups[0].tasks.length > 0) {
      setSelectedTaskId(groups[0].tasks[0].id);
    } else {
      setSelectedTaskId(null);
    }
  }, [allTasks, subcommitteeFilter]);

  if (loading) return <div style={s.emptyState}>Loading journey...</div>;
  if (error) return <div style={s.emptyState}>{error}</div>;
  if (dayGroups.length === 0 && !loading) return <div style={s.emptyState}>No tasks scheduled yet.</div>;

  const flatTasks = dayGroups.flatMap((d) => d.tasks);
  const selectedIdx = flatTasks.findIndex((t) => t.id === selectedTaskId);
  const selectedTask = selectedIdx >= 0 ? flatTasks[selectedIdx] : null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const nextIdx = e.key === 'ArrowDown'
        ? Math.min(selectedIdx + 1, flatTasks.length - 1)
        : Math.max(selectedIdx - 1, 0);
      if (flatTasks[nextIdx]) {
        setSelectedTaskId(flatTasks[nextIdx].id);
        const btn = listRef.current?.querySelector(`[data-task-id="${flatTasks[nextIdx].id}"]`) as HTMLElement;
        btn?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };

  const selectTask = (taskId: string) => {
    setSelectedTaskId(taskId);
    if (isMobile) setMobileDetailOpen(true);
  };

  return (
    <div style={s.container} onKeyDown={handleKeyDown} tabIndex={0} ref={listRef} role="application" aria-label="Journey task navigator">
      {/* Subcommittee pill filters */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button
          onClick={() => setSubcommitteeFilter('')}
          style={{ padding: '0.35rem 0.75rem', borderRadius: '16px', border: `1px solid ${subcommitteeFilter === '' ? PRIMARY : BORDER_LIGHT}`, backgroundColor: subcommitteeFilter === '' ? PRIMARY : BG_CARD, color: subcommitteeFilter === '' ? '#fff' : TEXT_MUTED, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          aria-pressed={subcommitteeFilter === ''}
        >All</button>
        {subcommittees.map((sc) => (
          <button
            key={sc.id}
            onClick={() => setSubcommitteeFilter(sc.id)}
            style={{ padding: '0.35rem 0.75rem', borderRadius: '16px', border: `1px solid ${subcommitteeFilter === sc.id ? PRIMARY : BORDER_LIGHT}`, backgroundColor: subcommitteeFilter === sc.id ? PRIMARY : BG_CARD, color: subcommitteeFilter === sc.id ? '#fff' : TEXT_MUTED, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
            aria-pressed={subcommitteeFilter === sc.id}
          >{sc.name}</button>
        ))}
      </div>

      {/* Print button */}
      <button
        onClick={() => {
          const filterLabel = subcommitteeFilter
            ? subcommittees.find((sc) => sc.id === subcommitteeFilter)?.name || 'Filtered'
            : 'All Subcommittees';
          let tableRows = '';
          dayGroups.forEach((day) => {
            day.tasks.forEach((task, idx) => {
              // Build assignee mini-table (Name | Role) with separator lines
              let assigneeHtml = '—';
              const aList = task.assignees && task.assignees.length > 0
                ? task.assignees.map((a: any) => ({ name: `${a.member.firstName} ${a.member.lastName}`, role: a.role?.name || '—' }))
                : task.assignee ? [{ name: `${task.assignee.firstName} ${task.assignee.lastName}`, role: '—' }] : [];
              if (aList.length > 0) {
                assigneeHtml = `<table style="border-collapse:collapse;font-size:10px;width:100%"><tr><th style="text-align:left;padding:2px 4px;border-bottom:1px solid #999">Name</th><th style="text-align:left;padding:2px 4px;border-bottom:1px solid #999">Role</th></tr>${aList.map((a: any) => `<tr><td style="padding:2px 4px;border-bottom:1px solid #ddd">${a.name}</td><td style="padding:2px 4px;border-bottom:1px solid #ddd">${a.role}</td></tr>`).join('')}</table>`;
              }
              const pColor = task.priority === 'High' ? '#d32f2f' : task.priority === 'Medium' ? '#f5a623' : '#2196f3';
              const priorityDot = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${pColor};margin-right:4px;vertical-align:middle"></span>${task.priority}`;
              const eventCell = task.eventName ? `${task.eventName}${task.eventTime ? '<br><span style="color:#666;font-size:10px;white-space:nowrap">' + task.eventTime + '</span>' : ''}` : '—';
              const fulfillment = (task as any).fulfillmentComments ? `<tr><td colspan="7" style="padding:3px 8px 6px;font-size:10px;color:#555;border-bottom:2px solid #e0e0e0"><em>Notes:</em> ${(task as any).fulfillmentComments}</td></tr>` : '';
              tableRows += `<tr${idx === 0 ? ' style="border-top:2px solid #333"' : ''}>
                <td>${idx === 0 ? day.dateLabel : ''}</td>
                <td style="width:20ch">${eventCell}</td>
                <td><strong>${task.title}</strong><br><span style="font-size:11px;color:#555">${task.description || ''}</span></td>
                <td>${task.status}</td>
                <td style="text-align:center">${priorityDot}</td>
                <td style="width:40ch">${assigneeHtml}</td>
                <td>${task.subcommittee?.name || ''}</td>
              </tr>${fulfillment}`;
            });
          });
          const html = `<!DOCTYPE html><html><head><title>Weekend Journey Tasks</title><style>
            body{font-family:sans-serif;padding:20px;font-size:12px}
            h1{font-size:16px;margin-bottom:4px}
            p{color:#666;font-size:11px;margin-bottom:12px}
            table{width:100%;border-collapse:collapse}
            th{text-align:left;padding:6px 8px;border-bottom:2px solid #333;font-size:11px;background:#f5f5f5}
            td{padding:5px 8px;border-bottom:1px solid #ddd;vertical-align:top;font-size:11px}
            td:nth-child(3){min-width:200px}
            td:nth-child(5){text-align:center}
            th:nth-child(5){text-align:center}
            @media print{body{padding:0}}
          </style></head><body>
            <h1>Weekend Journey Tasks — ${filterLabel}</h1>
            <p>${dayGroups.reduce((n, d) => n + d.tasks.length, 0)} tasks • Printed ${new Date().toLocaleDateString()}</p>
            <table><thead><tr><th>Date</th><th>Event / Time</th><th>Task / Details</th><th>Status</th><th>Priority</th><th>Assignees</th><th>Committee</th></tr></thead><tbody>${tableRows}</tbody></table>
          </body></html>`;
          const w = window.open('', '_blank');
          if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
        }}
        style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', border: `1px solid ${BORDER_LIGHT}`, backgroundColor: BG_CARD, color: TEXT_MUTED, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
        aria-label="Print weekend tasks"
      >🖨️ Print</button>

      {/* Mobile detail overlay */}
      {isMobile && mobileDetailOpen && selectedTask && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ backgroundColor: '#fff', width: '100%', maxHeight: '75vh', overflowY: 'auto', borderRadius: '16px 16px 0 0', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>Task Detail</span>
              <button onClick={() => setMobileDetailOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }} aria-label="Close detail">✕</button>
            </div>
            <TaskDetail task={selectedTask} />
          </div>
        </div>
      )}

      {dayGroups.map((day) => (
        <div key={day.dateKey} style={s.daySection}>
          <div style={s.dayHeader}>📅 {day.dateLabel}</div>
          <div style={isMobile ? s.twoPaneMobile : s.twoPane}>
            <div style={s.taskList}>
              {day.tasks.map((task) => {
                const isActive = task.id === selectedTaskId;
                const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.ToDo;
                return (
                  <button
                    key={task.id}
                    data-task-id={task.id}
                    style={isActive ? s.taskPillActive : s.taskPill}
                    onClick={() => selectTask(task.id)}
                    aria-label={`View task: ${task.title}`}
                    aria-pressed={isActive}
                  >
                    <div style={s.pillEventName}>{task.eventName} {task.eventTime && `· ${task.eventTime}`}</div>
                    <div style={s.pillTaskTitle}>{task.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.2rem' }}>
                      <span style={{ ...s.badge, backgroundColor: statusCfg.bg, color: statusCfg.text }}>{statusCfg.label}</span>
                      {task.priority && task.priority !== 'Medium' && (
                        <span style={{ fontSize: '0.65rem', color: PRIORITY_CONFIG[task.priority]?.color || TEXT_MUTED }}>{task.priority}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {!isMobile && (
              <div style={s.detailPane}>
                {selectedTask && day.tasks.some((t) => t.id === selectedTaskId) ? (
                  <TaskDetail task={selectedTask} />
                ) : (
                  <div style={s.detailEmpty}>Select a task or use ↑↓ arrows</div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Task Detail Sub-Component ─── */

function TaskDetail({ task }: { task: EventTask & { eventName: string; eventTime: string } }) {
  const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.ToDo;
  const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Medium;

  const assigneeList = task.assignees && task.assignees.length > 0
    ? task.assignees.map((a) => ({
        name: `${a.member.firstName} ${a.member.lastName}`,
        role: a.role?.name || null,
      }))
    : task.assignee
      ? [{ name: `${task.assignee.firstName} ${task.assignee.lastName}`, role: null }]
      : [];

  return (
    <div>
      <h3 style={s.detailTitle}>{task.title}</h3>

      {/* Status + Priority row */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ ...s.badge, backgroundColor: statusCfg.bg, color: statusCfg.text }}>{statusCfg.label}</span>
        <span style={{ ...s.badge, backgroundColor: '#f3f4f6', color: priorityCfg.color }}>{priorityCfg.label} Priority</span>
        {task.subcommittee && (
          <span style={{ ...s.badge, backgroundColor: BG_SURFACE, color: TEXT_MUTED }}>{task.subcommittee.name}</span>
        )}
      </div>

      {/* Event info */}
      <div style={s.detailSection}>
        <div style={s.detailLabel}>Event</div>
        <div style={s.detailValue}>{task.eventName}{task.eventTime && ` · ${task.eventTime}`}</div>
      </div>

      {/* Description */}
      {task.description && (
        <div style={s.detailSection}>
          <div style={s.detailLabel}>Description</div>
          <div style={{ ...s.detailValue, whiteSpace: 'pre-wrap' }}>{task.description}</div>
        </div>
      )}

      {/* Assignees */}
      {assigneeList.length > 0 && (
        <div style={s.detailSection}>
          <div style={s.detailLabel}>Assignees</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.3rem', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: TEXT_DARK }}>Name</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: TEXT_DARK }}>Role</th>
              </tr>
            </thead>
            <tbody>
              {assigneeList.map((a, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', color: TEXT_DARK }}>{a.name}</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', color: a.role ? TEXT_DARK : TEXT_MUTED, fontStyle: a.role ? 'normal' : 'italic' }}>{a.role || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Due Date */}
      {task.dueDate && (
        <div style={s.detailSection}>
          <div style={s.detailLabel}>Due Date</div>
          <div style={s.detailValue}>{formatDateLong(task.dueDate)}</div>
        </div>
      )}

      {/* Completed */}
      {task.completedAt && (
        <div style={s.detailSection}>
          <div style={s.detailLabel}>Completed</div>
          <div style={{ ...s.detailValue, color: '#059669' }}>{formatDateLong(task.completedAt)}</div>
        </div>
      )}

      {/* Category */}
      <div style={s.detailSection}>
        <div style={s.detailLabel}>Category</div>
        <div style={s.detailValue}>{task.category}</div>
      </div>
    </div>
  );
}
